"use client";
import { HeaderBlock, ThemeBlockPropertyCustom } from "@/components/cms/cms-shared-graphql-config/type";
import BleedBanner from "@/components/cms/component/bleedBanner";
import { useRootExperience } from "@/store/store";
import { HeaderContext } from "@/utils/providers/HeaderProvider";
import { getNationalId } from "@/utils/utilities";
import { HeaderCms } from "@nrldigitaltechnology/ui-community";
import { ServiceButtons } from "@nrldigitaltechnology/ui-community/dist/components/HeaderCms/types";
import React, { useContext, useEffect, useMemo, useState } from "react";

type HeaderMenuOptionsProps = {
  menuOptions: { name: string; url: string }[];
  subMenuOptions: Record<string, { name: string; url: string }[]>;
  serviceButtons?: ServiceButtons[];
};

type HeaderLayoutProps = {
  res: HeaderBlock | null;
  theming: ThemeBlockPropertyCustom | null;
  heroImage?: string;
};

export const HeaderLayout = ({ res, theming, heroImage }: HeaderLayoutProps) => {
  const headerRef = useContext(HeaderContext);
  const [showHeader, setShowHeader] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  const { setExperience } = useRootExperience();

  const [menuDetails, setMenuDetails] = useState<HeaderMenuOptionsProps>({
    menuOptions: [],
    subMenuOptions: {},
  });
  const nationalId = getNationalId();

  const headerLogo = nationalId === 32 ? "/assets/logo/playRugbyLeagueLogoNew.svg" : nationalId === 31 ? "/assets/logo/tfa_logo.png" : "";

  useEffect(() => {
    const heroImage = nationalId === 32 ? "/assets/common/poster.jpg" : nationalId === 31 ? "/assets/common/tfa_poster.jpeg" : "";
    setExperience({ heroImage });
  }, [])

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > lastScrollY && currentScrollY > 120) {
        setShowHeader(false);
      } else {
        setShowHeader(true);
      }
      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [lastScrollY]);


  function formatMenuOptions(objItem: any): {
    menuOptions: { name: string; url: string }[];
    subMenuOptions: Record<string, { name: string; url: string }[]>;
    serviceButtons?: ServiceButtons[];
  } {
    const menus: { name: string; url: string }[] = [];
    const subMenus: { menuKey: string; name: string; url: string }[] = [];
    const serviceBtns: ServiceButtons[] = [];

    function recurse(item: any, subMenuKey?: string) {
      if (Array.isArray(item)) {
        for (const el of item) {
          recurse(el, subMenuKey);
        }
      } else if (typeof item === "object" && item !== null) {
        if (item.__typename === "MenuGroupBlock") {
          const obj = {
            name: item.heading?.toLowerCase() || "",
            url: item?.menuUrl?.default ? `${item?.menuUrl?.default}` : "",
          };
          menus.push(obj);

          if (Array.isArray(item.menuContentArea)) {
            recurse(item.menuContentArea, obj.name);
          }
        } else if (item.__typename === "MenuNavigation") {
          subMenus.push({
            menuKey: subMenuKey || "",
            name: item.title,
            url: item.navigationLink?.default ? `${item.navigationLink?.default}` : "",
          });
        } else if (item.__typename === "ButtonBlock") {
          const obj = {
            name: item.buttonText || "",
            url: item?.buttonUrl?.default ? `${item?.buttonUrl?.default}` : "",
            variant: item.buttonType
          };
          serviceBtns.push(obj);
        }
        else {
          for (const key in item) {
            recurse(item[key], subMenuKey);
          }
        }
      }
    }

    recurse(objItem);
    const finalSubMenus: Record<string, { name: string; url: string }[]> = {};

    for (const submenu of subMenus) {
      const key = submenu.menuKey.toLowerCase();

      if (!finalSubMenus[key]) {
        finalSubMenus[key] = [];
      }
      finalSubMenus[key].push({
        name: submenu?.name || "",
        url: submenu?.url || "",
      });
    }

    return { menuOptions: menus, subMenuOptions: finalSubMenus, serviceButtons: serviceBtns };
  }

  useEffect(() => {
    if (!res) return;
    const initFormatRes = () => {
      const { menuOptions, subMenuOptions, serviceButtons } = formatMenuOptions(res);
      setMenuDetails({
        menuOptions,
        subMenuOptions,
        serviceButtons,
      });
    };
    initFormatRes();
  }, [res]);

  return (
    <>
      <div
        ref={headerRef}
        className={`w-full transition-transform duration-300 z-50 ${showHeader
          ? "translate-y-0 pointer-events-auto"
          : "-translate-y-full pointer-events-none"
          }`}
      >
        <HeaderCms
          headerTitle={nationalId === 31 ? "TOUCH FOOTBALL AUSTRALIA" : ""}
          leftIcon={headerLogo}
          menuOptions={menuDetails.menuOptions}
          subMenuOptions={menuDetails.subMenuOptions}
          serviceButtons={menuDetails.serviceButtons}
        />
        {
          res?.headerNotificationBanner?.__typename &&
          <BleedBanner data={res?.headerNotificationBanner || null} />
        }
      </div>

    </>
  );
};

export const menuOptions = [
  { name: "play", url: "play" },
  { name: "assist", url: "assist" },
  { name: "learn", url: "learn" },
  { name: "competitions" },
  { name: "coach", url: "coach" },
  { name: "safety", url: "safety" },
  { name: "shop", url: "shop" },
];

export const subMenuOptions = {
  play: [
    { name: "club rugby league", url: "club-rugby-league" },
    { name: "rewards", url: "rewards" },
    { name: "wheelchair rugby league", url: "wheelchair-rugby-league" },
    { name: "league stars", url: "league-stars" },
    { name: "little league stars", url: "little-league-stars" },
    { name: "school holiday clinics", url: "school-holiday-clinics" },
    { name: "child safety", url: "child-safety" },
  ],
  competitions: [
    { name: "club rugby league", url: "club-rugby-league" },
    { name: "rewards", url: "rewards" },
    { name: "wheelchair rugby league", url: "wheelchair-rugby-league" },
    { name: "league stars", url: "league-stars" },
    { name: "little league stars", url: "little-league-stars" },
    { name: "school holiday clinics", url: "school-holiday-clinics" },
    { name: "child safety", url: "child-safety" },
  ],
};

export const serviceButtons: ServiceButtons[] = [
  { name: "register", url: "register", variant: "primary" },
  { name: "log in", variant: "secondary" },
];
